import type { PixelPoint } from '../domain/bonds';
import type { BondProperties } from '../model';

export interface BondSegment {
  readonly start: PixelPoint;
  readonly end: PixelPoint;
  readonly aromaticInner?: boolean;
}

// #965: the one spacing between the two lines of every double bond, in a ring or not. Capped at a
// fraction of the bond's own rendered length, so the lines of a molecule scaled down to fit stay
// apart instead of merging into an illegible blob.
const BOND_LINE_GAP_PX = 7;

function bondLineGap(length: number): number {
  return Math.min(BOND_LINE_GAP_PX, length * 0.16);
}

// A triple bond keeps its tighter spacing.
const TRIPLE_BOND_LINE_GAP_PX = 5;

// Shared by aromatic bonds and plain (Kekule) double bonds that belong to exactly one ring (#422):
// the second line insets toward `ringCenter` instead of splitting symmetrically across the axis.
// `aromatic` only controls the returned inner segment's styling hook, not the inward-offset math,
// which is the same for both -- a ring is a ring regardless of aromaticity.
export function createRingAwareBondSegments(
  start: PixelPoint,
  end: PixelPoint,
  order: BondProperties['order'],
  ringCenter: PixelPoint,
  aromatic: boolean,
  insetStart = true,
  insetEnd = true
): readonly BondSegment[] {
  const outer = { start, end };
  if (order !== 2) return [outer];
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return [outer];
  const along = { x: dx / length, y: dy / length };
  const perpendicular = { x: -along.y, y: along.x };
  const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const inwardSign =
    (ringCenter.x - midpoint.x) * perpendicular.x + (ringCenter.y - midpoint.y) * perpendicular.y >= 0 ? 1 : -1;
  const inset = Math.min(8, length * 0.2);
  const offset = bondLineGap(length) * inwardSign;
  return [
    outer,
    {
      start: {
        x: start.x + along.x * (insetStart ? inset : 0) + perpendicular.x * offset,
        y: start.y + along.y * (insetStart ? inset : 0) + perpendicular.y * offset
      },
      end: {
        x: end.x - along.x * (insetEnd ? inset : 0) + perpendicular.x * offset,
        y: end.y - along.y * (insetEnd ? inset : 0) + perpendicular.y * offset
      },
      aromaticInner: aromatic
    }
  ];
}

// ADR 0024: a quadruple bond's four lines sit closer (#1127), a quintuple and sextuple bond's
// closer still (#1130), so each is about as wide as a triple one.
const QUADRUPLE_BOND_LINE_GAP_PX = 4;

function offsetsForOrder(order: BondProperties['order'], length: number): readonly number[] {
  if (order === 1) return [0];
  if (order === 2) return [-bondLineGap(length) / 2, bondLineGap(length) / 2];
  // Owner (#1130): triple to sextuple bonds keep a fixed gap whatever the bond's length; a gap that
  // shrank with a short bond merged the lines into one blob.
  if (order >= 4) {
    // Quadruple to sextuple (#1130): n lines evenly spread, a little closer the more there are.
    const gap = order === 4 ? QUADRUPLE_BOND_LINE_GAP_PX : order === 5 ? 3.5 : 3;
    return Array.from({ length: order }, (_, index) => (index - (order - 1) / 2) * gap);
  }
  return [-TRIPLE_BOND_LINE_GAP_PX, 0, TRIPLE_BOND_LINE_GAP_PX];
}

/** #1130: a quintuple or sextuple bond's lines are drawn thinner, so they stay apart at their closer gap. */
export function bondLineStrokeScale(order: number): number {
  return order === 5 ? 0.65 : order === 6 ? 0.55 : 1;
}

export function createBondSegments(
  start: PixelPoint,
  end: PixelPoint,
  order: BondProperties['order'],
  // B3 (#271): a crossed double bond -- its two lines drawn crossing (X) for a C=C whose
  // cis/trans is deliberately undrawn. Only meaningful for order 2; ignored otherwise.
  crossed = false
): readonly BondSegment[] {
  const length = Math.hypot(end.x - start.x, end.y - start.y);
  if (length === 0) return [{ start, end }];
  const perpendicular = { x: -(end.y - start.y) / length, y: (end.x - start.x) / length };
  const offsets = offsetsForOrder(order, length);
  return offsets.map((offset, index) => {
    // Flip the end offset for the second line so the pair crosses at the midpoint.
    const endOffset = crossed && order === 2 ? offsets[offsets.length - 1 - index]! : offset;
    return {
      start: {
        x: start.x + perpendicular.x * offset,
        y: start.y + perpendicular.y * offset
      },
      end: {
        x: end.x + perpendicular.x * endOffset,
        y: end.y + perpendicular.y * endOffset
      }
    };
  });
}

/**
 * #1130, #1187 (owner): multi-line bonds (double through hextuple) end square. Each line is first
 * trimmed on its own against the atom labels (#965); against a label's rounded outline the outer
 * lines of a multi-line bond end unevenly. This puts every line's ends where the most-trimmed line's
 * are -- measured along the bond axis -- so all lines end square, at the same length, and clear
 * the labels.
 */
export function evenParallelSegments(
  untrimmed: readonly BondSegment[],
  trimmed: readonly BondSegment[]
): readonly BondSegment[] {
  const axis = untrimmed[0];
  if (!axis || untrimmed.length !== trimmed.length) return trimmed;
  const dx = axis.end.x - axis.start.x;
  const dy = axis.end.y - axis.start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return trimmed;

  const alongAxis = (point: PixelPoint) =>
    ((point.x - axis.start.x) * dx + (point.y - axis.start.y) * dy) / lengthSquared;

  const untrimmedStarts = untrimmed.map((line) => alongAxis(line.start));
  const untrimmedEnds = untrimmed.map((line) => alongAxis(line.end));
  const trimmedStarts = trimmed.map((line) => alongAxis(line.start));
  const trimmedEnds = trimmed.map((line) => alongAxis(line.end));

  const startTrimmed = trimmedStarts.some((t, i) => t > untrimmedStarts[i]! + 1e-4);
  const endTrimmed = trimmedEnds.some((t, i) => t < untrimmedEnds[i]! - 1e-4);

  if (!startTrimmed && !endTrimmed) {
    return trimmed;
  }

  // At each end that was trimmed by a label, line endings align to the most-trimmed projection.
  // Lines that originate at the bond start (untrimmed near 0) align to max(trimmedStarts).
  const maxStart = startTrimmed ? Math.max(...trimmedStarts.filter((_, i) => untrimmedStarts[i]! <= 0.05)) : 0;
  // Lines that terminate at the bond end (untrimmed near 1) align to min(trimmedEnds).
  const minEnd = endTrimmed ? Math.min(...trimmedEnds.filter((_, i) => untrimmedEnds[i]! >= 0.95)) : 1;

  return untrimmed.map((line, index) => {
    const t0Start = untrimmedStarts[index]!;
    const t0End = untrimmedEnds[index]!;

    const targetStart = startTrimmed && t0Start <= 0.05 ? Math.max(t0Start, maxStart) : trimmedStarts[index]!;

    const targetEnd = endTrimmed && t0End >= 0.95 ? Math.min(t0End, minEnd) : trimmedEnds[index]!;

    if (targetEnd <= targetStart) {
      return trimmed[index]!;
    }

    return {
      ...line,
      start: {
        x: line.start.x + dx * (targetStart - t0Start),
        y: line.start.y + dy * (targetStart - t0Start)
      },
      end: {
        x: line.start.x + dx * (targetEnd - t0Start),
        y: line.start.y + dy * (targetEnd - t0Start)
      }
    };
  });
}

export function distanceToSegment(point: PixelPoint, start: PixelPoint, end: PixelPoint): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(point.x - start.x, point.y - start.y);
  const projection = Math.min(1, Math.max(0, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (start.x + projection * dx), point.y - (start.y + projection * dy));
}

// #1075: a terminal double bond follows the inside of its single-bond bend.
// Work in rendered pixels so the side is correct on non-square paper and in exports.
export function terminalDoubleBondCenters(
  bonds: readonly (BondSegment & { readonly id: string; readonly order: BondProperties['order'] })[]
): Readonly<
  Record<
    string,
    PixelPoint & { readonly jointAtStart: boolean; readonly insetStart: boolean; readonly insetEnd: boolean }
  >
> {
  const centers: Record<
    string,
    PixelPoint & { readonly jointAtStart: boolean; readonly insetStart: boolean; readonly insetEnd: boolean }
  > = {};
  const near = (a: PixelPoint, b: PixelPoint) => Math.hypot(a.x - b.x, a.y - b.y) < 0.5;
  for (const bond of bonds) {
    if (bond.order !== 2) continue;
    const neighbors = (point: PixelPoint) =>
      bonds.filter((other) => other.id !== bond.id && (near(point, other.start) || near(point, other.end)));
    const atStart = neighbors(bond.start);
    const atEnd = neighbors(bond.end);
    // Prefer the bend beside a terminal single bond, even when the double
    // bond itself is internal to a chain. Branched junctions stay symmetric.
    if (atStart.length > 1 || atEnd.length > 1) continue;
    const candidates = [
      ...atStart.map((neighbor) => ({ neighbor, joint: bond.start })),
      ...atEnd.map((neighbor) => ({ neighbor, joint: bond.end }))
    ].flatMap(({ neighbor, joint }) => {
      if (neighbor.order !== 1) return [];
      const facing = near(joint, neighbor.start) ? neighbor.end : neighbor.start;
      const terminalSingle = !bonds.some(
        (other) => other.id !== neighbor.id && (near(facing, other.start) || near(facing, other.end))
      );
      const terminalDouble = atStart.length === 0 || atEnd.length === 0;
      if (!terminalSingle && !terminalDouble) return [];
      const cross =
        (bond.end.x - bond.start.x) * (facing.y - joint.y) - (bond.end.y - bond.start.y) * (facing.x - joint.x);
      return Math.abs(cross) > 1e-6 ? [{ facing, joint, sign: Math.sign(cross) }] : [];
    });
    const chosen = candidates[0];
    // Opposing bends have no common inner side.
    if (!chosen || candidates.some((candidate) => candidate.sign !== chosen.sign)) continue;
    centers[bond.id] = {
      ...chosen.facing,
      jointAtStart: chosen.joint === bond.start,
      insetStart: atStart.length > 0,
      insetEnd: atEnd.length > 0
    };
  }
  return centers;
}
